<template>
	<div class="overlay-stage-area" ref="area" @mousedown="onAreaMouseDown" @dblclick="onAreaDblClick" @contextmenu="onContextMenu">
		<div
			class="overlay-stage"
			ref="stage"
			:style="{ width: `${overlay.width * scale}px`, height: `${overlay.height * scale}px` }"
			@mousedown="onStageMouseDown"
			@click="onStageClick"
			@dblclick="onStageDblClick"
		>
			<div
				v-for="layer in renderLayers"
				:key="layer.id"
				class="overlay-stage-layer"
				:class="{ selected: layer.id === selectedId, locked: layer.locked, 'snap-target': snapIds.includes(layer.id) }"
				:data-layer-id="layer.id"
				:style="boxStyle(layer)"
			>
				<overlay-layer-view :layer="layer" :overlay="overlay" :scale="scale" :preview-shared="previewShared" :editing="layer.id === editingId" :edit-point="layer.id === editingId ? editPoint : null" @edit-end="onEditEnd(layer, $event)" />
			</div>
			<div v-if="snapEdges.left" class="overlay-edge-highlight edge-left"></div>
			<div v-if="snapEdges.right" class="overlay-edge-highlight edge-right"></div>
			<div v-if="snapEdges.top" class="overlay-edge-highlight edge-top"></div>
			<div v-if="snapEdges.bottom" class="overlay-edge-highlight edge-bottom"></div>
		</div>
		<div v-if="selectedLayer && selectedLayer.visible && !hideToolbar" class="overlay-stage-toolbar" :style="toolbarStyle" @mousedown.stop @click.stop @dblclick.stop @contextmenu.stop.prevent>
			<slot name="toolbar" :layer="selectedLayer" />
		</div>
	</div>
</template>

<script>
import Moveable from 'moveable';
import OverlayLayerView from './OverlayLayerView.vue';
import { getWarpFromCorners } from '@/overlayWarp';
import { startCropResize, applyCropResize, cropContentStyle, isFullCrop } from '@/overlayCrop';
import { layerBoxStyle, layerBounds, layerWarpTransform, pointInLayer } from './layerGeometry';
import { isMediaLayer, keepsAspectRatio } from './layerTypes';

const SNAP_ALL = { left: true, top: true, right: true, bottom: true, center: true, middle: true };
const CLICK_SLOP = 3;
// The rotate handle: a 40px line plus the 14px knob at its end.
const ROTATE_HANDLE_REACH = 48;
const TOOLBAR_GAP = 10;
// Moveable's own resize direction, e.g. [-1, -1], turned into 'nw'/'n'/'w'/... to match overlayCrop's handle names.
const directionToHandle = ([ dx, dy ]) => (dy < 0 ? 'n' : dy > 0 ? 's' : '') + (dx < 0 ? 'w' : dx > 0 ? 'e' : '');

export default {
	components: { OverlayLayerView },
	props: {
		overlay: { type: Object, required: true },
		scene: { type: Object, default: null },
		selectedId: { type: Number, default: null },
		// One shared services instance for every widget layer's live preview; null renders placeholders instead.
		previewShared: { type: Object, default: null },
		// The layer whose corners are being dragged (perspective distort mode).
		warpId: { type: Number, default: null },
		// The layer whose crop rectangle is being edited.
		cropId: { type: Number, default: null },
		// The layer that resizes by stretching (ignoring its own aspect ratio) without needing Shift held.
		stretchId: { type: Number, default: null },
		// Margin kept around the canvas when fitting it to the available area - 0 for an exact 1:1 fit
		// (e.g. a fullscreened preview matching its screen's resolution), the normal editor's own padding otherwise.
		pad: { type: Number, default: 48 },
	},
	data() { return {
		areaW: 800,
		areaH: 600,
		stageLeft: 0,
		stageTop: 0,
		interacting: false,
		editingId: null,
		editPoint: null,
		hideToolbar: false,
		live: null,
		snapIds: [],
		snapEdges: {},
		shiftHeld: false,
		altHeld: false,
	}},
	computed: {
		layers() { return this.scene ? this.scene.layers : []; },
		scale() {
			const k = Math.min((this.areaW - this.pad * 2) / this.overlay.width, (this.areaH - this.pad * 2) / this.overlay.height);
			return Math.max(0.05, k);
		},
		renderLayers() {
			return this.layers.filter(l => l.visible).sort((a, b) => a.sort_order - b.sort_order);
		},
		selectedLayer() {
			return this.layers.find(l => l.id === this.selectedId) || null;
		},
		// Crop mode makes every resize handle crop without needing Alt; Alt still works as a one-off outside it.
		cropActive() {
			return this.cropId !== null || this.altHeld;
		},
		syncKey() {
			const s = this.selectedLayer;
			return [
				this.scale, this.overlay.width, this.overlay.height,
				s && [ s.id, s.x, s.y, s.w, s.h, s.rotation, s.visible, s.locked, JSON.stringify(s.props?.warp), JSON.stringify(s.props?.crop), this.warpId ].join(','),
				this.renderLayers.map(l => `${l.id}:${l.x},${l.y},${l.w},${l.h},${l.rotation},${JSON.stringify(l.props?.warp)}`).join(';'),
			].join('|');
		},
		toolbarStyle() {
			// While a layer is being dragged the toolbar follows it rather than disappearing and popping back.
			const s = this.live || this.selectedLayer;
			if(!s) return {};
			const b = layerBounds(s);
			const k = this.scale;
			const cx = Math.min(Math.max(this.stageLeft + (b.left + b.right) / 2 * k, 160), this.areaW - 160);
			// Moveable's rotate handle sticks out of the layer's top edge (turning with the layer); keep the toolbar clear of it.
			// Distorted layers and the distort mode itself have none.
			const hasRotateHandle = !this.selectedLayer?.props?.warp && this.warpId !== this.selectedId;
			const theta = (s.rotation || 0) * Math.PI / 180;
			const handleY = (s.y + s.h / 2) * k - (s.h * k / 2 + (hasRotateHandle ? ROTATE_HANDLE_REACH : 0)) * Math.cos(theta);
			const top = Math.min(b.top * k, handleY);
			const bottom = Math.max(b.bottom * k, handleY);
			const above = this.stageTop + top - TOOLBAR_GAP;
			if(above > 40) return { left: `${cx}px`, top: `${above}px`, transform: 'translate(-50%, -100%)' };
			return { left: `${cx}px`, top: `${this.stageTop + bottom + TOOLBAR_GAP}px`, transform: 'translate(-50%, 0)' };
		},
	},
	watch: {
		syncKey() { this.$nextTick(this.syncMoveable); },
		shiftHeld() { this.applyKeepRatio(); },
		stretchId() { this.applyKeepRatio(); },
		selectedId(id) {
			// Blurring commits the text, which in turn ends the edit.
			if(this.editingId !== null && id !== this.editingId) this.getLayerEl(this.editingId)?.querySelector('[contenteditable]')?.blur();
		},
		// A different look for the handles while a drag on them would crop rather than resize.
		cropActive(active) {
			if(this.moveable) this.moveable.className = active ? 'moveable-crop' : '';
		},
	},
	mounted() {
		this.frame = { translate: [ 0, 0 ], rotate: 0, warp: '' };
		this.cropDrag = null;
		this.down = null;
		this.lastClick = null;
		this.justSelected = false;
		this.pointerDown = false;
		this.onPointerUp = () => {
			this.pointerDown = false;
			// The click that follows a press-select may land on moveable's own box (outside the stage) and never reach
			// onStageClick, which would otherwise leave this set and swallow the next click. The click fires in the same
			// task as this mouseup, so clearing on the next tick is safe.
			setTimeout(() => { this.justSelected = false; }, 0);
		};
		window.addEventListener('mouseup', this.onPointerUp, true);
		this.onModifierKeys = e => { this.shiftHeld = e.shiftKey; this.altHeld = e.altKey; };
		this.onWindowBlur = () => { this.shiftHeld = false; this.altHeld = false; };
		window.addEventListener('keydown', this.onModifierKeys);
		window.addEventListener('keyup', this.onModifierKeys);
		// Pointer events carry the modifier state too, which covers a key pressed while something else had the focus.
		window.addEventListener('mousemove', this.onModifierKeys, true);
		window.addEventListener('mousedown', this.onModifierKeys, true);
		window.addEventListener('blur', this.onWindowBlur);
		this.resizeObserver = new ResizeObserver(() => this.measure());
		this.resizeObserver.observe(this.$refs.area);
		this.measure();
		this.createMoveable();
		this.$nextTick(this.syncMoveable);
	},
	beforeDestroy() {
		window.removeEventListener('mouseup', this.onPointerUp, true);
		window.removeEventListener('keydown', this.onModifierKeys);
		window.removeEventListener('keyup', this.onModifierKeys);
		window.removeEventListener('mousemove', this.onModifierKeys, true);
		window.removeEventListener('mousedown', this.onModifierKeys, true);
		window.removeEventListener('blur', this.onWindowBlur);
		this.resizeObserver?.disconnect();
		this.moveable?.destroy();
	},
	methods: {
		boxStyle(layer) { return layerBoxStyle(layer, this.scale); },
		measure() {
			const area = this.$refs.area;
			if(!area) return;
			this.areaW = area.clientWidth;
			this.areaH = area.clientHeight;
			this.$nextTick(() => {
				this.stageLeft = this.$refs.stage.offsetLeft;
				this.stageTop = this.$refs.stage.offsetTop;
			});
		},
		getLayerEl(id) {
			return this.$refs.stage.querySelector(`[data-layer-id="${id}"]`);
		},
		createMoveable() {
			const m = this.moveable = new Moveable(this.$refs.area, {
				target: null,
				container: this.$refs.area,
				draggable: true,
				resizable: true,
				rotatable: true,
				warpable: false,
				snappable: true,
				origin: false,
				keepRatio: false,
				throttleDrag: 0,
				throttleResize: 0,
				throttleRotate: 0,
				rotationPosition: 'top',
				renderDirections: [ 'nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se' ],
				edge: false,
				snapContainer: this.$refs.stage,
				snapDirections: SNAP_ALL,
				elementSnapDirections: SNAP_ALL,
				snapThreshold: 6,
				isDisplaySnapDigit: false,
				snapGap: false,
				elementGuidelines: [],
				horizontalGuidelines: [],
				verticalGuidelines: [],
			});
			m.on('dragStart', e => { this.beginInteraction(); e.set(this.frame.translate); });
			m.on('drag', e => {
				this.frame.translate = e.beforeTranslate;
				this.applyTransform(e.target);
				const l = this.selectedLayer;
				if(l) this.live = { x: this.frame.translate[0] / this.scale, y: this.frame.translate[1] / this.scale, w: l.w, h: l.h, rotation: l.rotation };
			});
			m.on('dragEnd', e => this.endInteraction(e.isDrag));
			m.on('resizeStart', e => {
				this.beginInteraction();
				const layer = this.selectedLayer;
				// Alt (or crop mode, which is the same thing without needing to hold it) turns the ordinary resize
				// handles into crop handles, same as OBS: drag an edge in and it's trimmed, not scaled.
				this.cropDrag = layer && isMediaLayer(layer) && this.cropActive ? {
					layerId: layer.id,
					handle: directionToHandle(e.direction),
					start: startCropResize(layer),
					startClient: [ e.clientX, e.clientY ],
					cos: Math.cos(-(layer.rotation || 0) * Math.PI / 180),
					sin: Math.sin(-(layer.rotation || 0) * Math.PI / 180),
				} : null;
				if(this.cropDrag) return;
				e.setOrigin([ '%', '%' ]);
				e.dragStart && e.dragStart.set(this.frame.translate);
				// Proportional resizing follows the media's own shape, not the (possibly stretched) shape the layer has now.
				const ratio = this.getMediaRatio(e.target);
				if(ratio) e.setRatio(ratio);
			});
			m.on('resize', e => {
				this.hideToolbar = true;
				if(this.cropDrag) {
					this.tickCropDrag(e);
					return;
				}
				e.target.style.width = `${e.width}px`;
				e.target.style.height = `${e.height}px`;
				this.frame.translate = e.drag.beforeTranslate;
				this.applyTransform(e.target);
			});
			m.on('resizeEnd', e => {
				if(this.cropDrag) {
					this.endCropDrag(e);
					return;
				}
				this.endInteraction(e.isDrag);
			});
			m.on('rotateStart', e => { this.beginInteraction(); e.set(this.frame.rotate); });
			m.on('rotate', e => { this.hideToolbar = true; this.frame.rotate = e.beforeRotate; this.applyTransform(e.target); });
			m.on('rotateEnd', e => this.endInteraction(e.isDrag));
			m.on('warpStart', () => this.beginInteraction());
			m.on('warp', e => {
				this.hideToolbar = true;
				e.target.style.transform = e.transform;
			});
			m.on('warpEnd', e => this.endWarp(e));
			m.on('snap', e => this.onSnap(e));
		},
		// One tick of a crop-handle drag: works from the fixed snapshot taken at resizeStart, not the previous tick,
		// so it can't drift. Applied straight to the DOM for a live preview; only saved (via `endCropDrag`) on release.
		tickCropDrag(e) {
			const { start, handle, startClient, cos, sin } = this.cropDrag;
			const rawDx = e.clientX - startClient[0], rawDy = e.clientY - startClient[1];
			const k = this.scale;
			const dx = (rawDx * cos - rawDy * sin) / k;
			const dy = (rawDx * sin + rawDy * cos) / k;
			const result = applyCropResize(start, handle)(dx, dy);
			this.cropDrag.result = result;
			this.frame.translate = [ result.x * k, result.y * k ];
			e.target.style.width = `${result.w * k}px`;
			e.target.style.height = `${result.h * k}px`;
			this.applyTransform(e.target);
			const media = e.target.querySelector('img, video');
			if(media) Object.assign(media.style, cropContentStyle(result.crop));
		},
		endCropDrag(e) {
			this.interacting = false;
			this.hideToolbar = false;
			this.live = null;
			const { layerId, result } = this.cropDrag;
			this.cropDrag = null;
			if(e.isDrag && result) this.$emit('crop', layerId, { ...result, crop: isFullCrop(result.crop) ? null : result.crop });
			this.$nextTick(this.syncMoveable);
		},
		getMediaRatio(el) {
			const media = el.querySelector('img, video');
			const w = media?.naturalWidth || media?.videoWidth;
			const h = media?.naturalHeight || media?.videoHeight;
			return w && h ? w / h : 0;
		},
		// Images and videos resize proportionally; holding shift (or stretch mode, the same without holding it)
		// stretches them instead, like OBS. Other layers always stretch.
		applyKeepRatio(layer = this.selectedLayer) {
			if(!this.moveable) return;
			const stretching = this.shiftHeld || (!!layer && this.stretchId === layer.id);
			this.moveable.keepRatio = !!layer && keepsAspectRatio(layer) && !stretching;
		},
		applyTransform(el) {
			el.style.transform = `translate(${this.frame.translate[0]}px, ${this.frame.translate[1]}px) rotate(${this.frame.rotate}deg) ${this.frame.warp}`.trim();
		},
		beginInteraction() {
			this.interacting = true;
		},
		endInteraction(isDrag) {
			this.interacting = false;
			this.hideToolbar = false;
			this.live = null;
			this.snapIds = [];
			this.snapEdges = {};
			const el = this.moveable.target;
			if(!isDrag || !el || !this.selectedLayer) {
				this.$nextTick(this.syncMoveable);
				return;
			}
			const k = this.scale;
			const rotation = ((Math.round(this.frame.rotate) % 360) + 360) % 360;
			this.$emit('geometry', this.selectedLayer.id, {
				x: Math.round(this.frame.translate[0] / k),
				y: Math.round(this.frame.translate[1] / k),
				w: Math.max(1, Math.round(parseFloat(el.style.width) / k)),
				h: Math.max(1, Math.round(parseFloat(el.style.height) / k)),
				rotation: rotation > 180 ? rotation - 360 : rotation,
			});
		},
		// Reads where the corners ended up back into the layer's own frame and hands them to the editor as offsets.
		endWarp(e) {
			this.interacting = false;
			this.hideToolbar = false;
			this.live = null;
			this.snapIds = [];
			this.snapEdges = {};
			const layer = this.selectedLayer;
			if(!e.isDrag || !layer) {
				this.$nextTick(this.syncMoveable);
				return;
			}
			this.moveable.updateRect();
			const { pos1, pos2, pos3, pos4 } = this.moveable.getRect();
			const k = this.scale;
			const rad = -(layer.rotation || 0) * Math.PI / 180;
			const cos = Math.cos(rad);
			const sin = Math.sin(rad);
			const corners = [ pos1, pos2, pos4, pos3 ].map(([ px, py ]) => {
				const dx = (px - this.stageLeft) / k - (layer.x + layer.w / 2);
				const dy = (py - this.stageTop) / k - (layer.y + layer.h / 2);
				return [ dx * cos - dy * sin, dx * sin + dy * cos ];
			});
			this.$emit('warp', layer.id, getWarpFromCorners(layer.w, layer.h, corners));
		},
		onSnap(e) {
			const ids = new Set();
			for(const g of e.elements || []) {
				const id = g.element?.getAttribute('data-layer-id');
				if(id) ids.add(Number(id));
			}
			const W = this.overlay.width * this.scale;
			const H = this.overlay.height * this.scale;
			const near = (a, b) => Math.abs(a - b) < 1.5;
			const edges = {};
			// Guideline positions come back relative to the moveable container (the area), not the stage.
			for(const g of e.guidelines || []) {
				if(g.type === 'vertical') {
					const x = g.pos[0] - this.stageLeft;
					if(near(x, 0)) edges.left = true;
					else if(near(x, W)) edges.right = true;
				} else {
					const y = g.pos[1] - this.stageTop;
					if(near(y, 0)) edges.top = true;
					else if(near(y, H)) edges.bottom = true;
				}
			}
			this.snapIds = [ ...ids ];
			this.snapEdges = edges;
		},
		syncMoveable() {
			if(!this.moveable || this.interacting) return;
			this.attachMoveable(this.selectedLayer);
		},
		// Point moveable at a layer (or at nothing). Also used to grab a layer that isn't selected yet.
		attachMoveable(layer) {
			const m = this.moveable;
			const el = layer && layer.visible && !layer.locked ? this.getLayerEl(layer.id) : null;
			if(!el) {
				m.target = null;
				return null;
			}
			const k = this.scale;
			this.frame.translate = [ layer.x * k, layer.y * k ];
			this.frame.rotate = layer.rotation || 0;
			this.frame.warp = layerWarpTransform(layer, k);
			// A distorted layer can be moved, but resizing and rotating it would fight the perspective.
			const warping = this.warpId === layer.id;
			const plain = !warping && !layer.props?.warp;
			this.applyKeepRatio(layer);
			m.warpable = warping;
			m.draggable = !warping;
			m.resizable = plain;
			m.rotatable = plain;
			m.horizontalGuidelines = [ 0, this.overlay.height * k / 2, this.overlay.height * k ];
			m.verticalGuidelines = [ 0, this.overlay.width * k / 2, this.overlay.width * k ];
			m.elementGuidelines = this.renderLayers.filter(l => l.id !== layer.id).map(l => this.getLayerEl(l.id)).filter(Boolean);
			m.target = el;
			m.updateRect();
			return el;
		},
		pointerToOverlay(e) {
			const rect = this.$refs.stage.getBoundingClientRect();
			return [ (e.clientX - rect.left) / this.scale, (e.clientY - rect.top) / this.scale ];
		},
		hitsAt(e) {
			const [ px, py ] = this.pointerToOverlay(e);
			return this.renderLayers.filter(l => !l.locked && pointInLayer(l, px, py)).reverse();
		},
		// True for events inside the text layer being edited, which belong to the text field, not to the stage.
		isEditingEvent(e) {
			return this.editingId !== null && !!this.getLayerEl(this.editingId)?.contains(e.target);
		},
		startEditing(layer, e) {
			this.editPoint = e ? { x: e.clientX, y: e.clientY } : null;
			this.editingId = layer.id;
		},
		onEditEnd(layer, text) {
			if(this.editingId === layer.id) this.editingId = null;
			if(text !== null && text !== (layer.props.text || '')) this.$emit('text', layer.id, text);
		},
		onStageMouseDown(e) {
			if(e.button !== 0 || this.isEditingEvent(e)) return;
			this.down = { x: e.clientX, y: e.clientY };
			this.pointerDown = true;
			this.pressedEmpty = false;
			const hits = this.hitsAt(e);
			if(!hits.length) {
				this.pressedEmpty = true;
				// Pressing on empty canvas deselects right away, whatever the pointer does afterwards.
				this.$emit('select', null);
				this.lastClick = null;
				return;
			}
			const selectedHit = hits.find(l => l.id === this.selectedId);
			if(!selectedHit) {
				this.$emit('select', hits[0].id);
				this.justSelected = true;
				this.lastClick = { x: e.clientX, y: e.clientY };
				// Grab it right away so pressing and dragging an unselected layer moves it. Moveable has to finish
				// switching targets before it can take over the drag, and the button may be released by then.
				this.grabLayer(hits[0], e);
			} else if(!this.getLayerEl(selectedHit.id)?.contains(e.target)) {
				this.moveable.dragStart(e);
			}
		},
		// Press-and-drag on a layer that isn't selected: select it, point moveable at it and take over the drag.
		grabLayer(layer, e) {
			// A gesture left half-finished (e.g. a release outside the window) would make moveable ignore the new one.
			if(this.moveable.isDragging?.()) this.moveable.stopDrag();
			const changed = this.moveable.waitToChangeTarget();
			const el = this.attachMoveable(layer);
			if(!el) return;
			// Moveable rebuilds its gestures once the target change settles; starting before that drops the drag. The
			// change promise never resolves if nothing changed, so don't wait on it forever.
			Promise.race([ changed, new Promise(resolve => setTimeout(resolve, 60)) ]).then(() => requestAnimationFrame(() => {
				if(this.pointerDown) this.moveable.dragStart(e, el);
			}));
		},
		onStageClick(e) {
			if(this.isEditingEvent(e)) return;
			// The press already deselected; a slightly wandering pointer must not turn the release into a selection.
			if(this.pressedEmpty) return;
			if(this.justSelected) {
				this.justSelected = false;
				return;
			}
			const moved = !this.down || Math.hypot(e.clientX - this.down.x, e.clientY - this.down.y) > CLICK_SLOP;
			if(moved) {
				this.lastClick = null;
				return;
			}
			const hits = this.hitsAt(e);
			if(!hits.length) {
				this.$emit('select', null);
				this.lastClick = null;
				return;
			}
			const sameSpot = this.lastClick && Math.hypot(e.clientX - this.lastClick.x, e.clientY - this.lastClick.y) <= CLICK_SLOP;
			const selectedIndex = hits.findIndex(l => l.id === this.selectedId);
			if(sameSpot && selectedIndex >= 0) {
				// The second click of a double-click opens properties; it must not also step to the layer underneath.
				if(e.detail < 2) this.$emit('select', hits[(selectedIndex + 1) % hits.length].id);
			} else {
				this.$emit('select', hits[0].id);
			}
			this.lastClick = { x: e.clientX, y: e.clientY };
		},
		onStageDblClick(e) {
			if(this.isEditingEvent(e)) return;
			const hits = this.hitsAt(e);
			const target = hits.find(l => l.id === this.selectedId) || hits[0];
			if(!target) return;
			// Text is also edited in place, with the caret where the double-click landed.
			if(target.type === 'text') this.startEditing(target, e);
			this.$emit('properties', target.id, e);
		},
		onAreaMouseDown(e) {
			if(e.button === 0 && e.target === this.$refs.area) this.$emit('select', null);
		},
		// Double-clicking Moveable's own rotate handle (not the layer itself) straightens the layer back out.
		onAreaDblClick(e) {
			if(!e.target.closest('.moveable-rotation-control')) return;
			const layer = this.selectedLayer;
			if(!layer || !layer.rotation) return;
			this.frame.rotate = 0;
			this.$emit('geometry', layer.id, { x: layer.x, y: layer.y, w: layer.w, h: layer.h, rotation: 0 });
		},
		onContextMenu(e) {
			// The browser's own menu (copy, paste...) stays available while typing in a text layer.
			if(this.isEditingEvent(e)) return;
			e.preventDefault();
			const rect = this.$refs.stage.getBoundingClientRect();
			const inside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
			const hits = inside ? this.hitsAt(e) : [];
			let layerId = null;
			if(hits.length) {
				layerId = hits.some(l => l.id === this.selectedId) ? this.selectedId : hits[0].id;
				if(layerId !== this.selectedId) this.$emit('select', layerId);
			}
			this.$emit('context', e, { layerId, hits, point: inside ? this.pointerToOverlay(e) : null });
		},
	},
};
</script>

<style lang="scss" scoped>
@import '../../plugins/variable.scss';

.overlay-stage-area {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	overflow: hidden;
	background: $body-bg;
}

.overlay-stage {
	position: relative;
	flex: 0 0 auto;
	// Transparent by default, drawn as a checkerboard.
	background: repeating-conic-gradient(#4a4a60 0% 25%, #383850 0% 50%) 0 0 / 20px 20px;
	box-shadow: 0 0 0 1px rgba(255, 255, 255, .15);
	overflow: hidden;
}

.overlay-stage-layer {
	&:hover { outline: 1px solid rgba(74, 170, 255, .7); }
	&.locked { pointer-events: none; }
	&.snap-target {
		outline: 3px solid #ffb400;
		outline-offset: -1px;
		box-shadow: inset 0 0 0 9999px rgba(255, 180, 0, .18);
	}
}

.overlay-edge-highlight {
	position: absolute;
	background: #ffb400;
	box-shadow: 0 0 10px 2px #ffb400;
	pointer-events: none;
	z-index: 5;

	&.edge-left { left: 0; top: 0; bottom: 0; width: 4px; }
	&.edge-right { right: 0; top: 0; bottom: 0; width: 4px; }
	&.edge-top { left: 0; right: 0; top: 0; height: 4px; }
	&.edge-bottom { left: 0; right: 0; bottom: 0; height: 4px; }
}

.overlay-stage-toolbar {
	position: absolute;
	z-index: 200;
}

// Moveable's default z-index (3000) draws the selection box over modals and menus.
.overlay-stage-area ::v-deep .moveable-control-box {
	z-index: 100 !important;
}

// A different look for the handles while a drag on them crops instead of resizes (crop mode, or Alt held).
.overlay-stage-area ::v-deep .moveable-crop {
	.moveable-control {
		background: #ffb400 !important;
		border-radius: 3px !important;
	}

	.moveable-line {
		background: #ffb400 !important;
	}
}
</style>
